/**
 * Admin Deliveries API client — FE-21.
 * Calls GET /admin/deliveries, GET /admin/deliveries/:id,
 * POST /admin/deliveries/:id/reindex, POST /admin/deliveries/:id/unhide,
 * POST /admin/deliveries/:id/unpin.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type SourceType = "artifact" | "file" | "media" | "link";

export type DeliverySummary = {
  readonly id: string;
  readonly title: string;
  readonly source_type: SourceType;
  readonly user_id: string;
  readonly user_name: string;
  readonly conversation_id: string;
  readonly pinned: boolean;
  readonly hidden: boolean;
  readonly deleted_at: string | null;
  readonly created_at: string;
};

export type DeliveryDetail = {
  readonly id: string;
  readonly title: string;
  readonly source_type: SourceType;
  readonly user_id: string;
  readonly user_name: string;
  readonly conversation_id: string;
  readonly conversation_title: string | null;
  readonly pinned: boolean;
  readonly hidden: boolean;
  readonly size_bytes: number;
  readonly mime_type: string | null;
  readonly deleted_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
};

// ─── Params ───

type ListDeliveriesParams = {
  readonly source_type?: SourceType;
  readonly user_id?: string;
  readonly limit?: number;
  readonly offset?: number;
};

type ListDeliveriesResponse = {
  readonly items: DeliverySummary[];
  readonly total: number;
};

// ─── API calls ───

export async function fetchDeliveries(
  params: ListDeliveriesParams,
): Promise<ListDeliveriesResponse> {
  const sp = new URLSearchParams();
  if (params.source_type) sp.set("source_type", params.source_type);
  if (params.user_id) sp.set("user_id", params.user_id);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString();
  const res = await apiFetch<{ data: ListDeliveriesResponse }>(
    qs ? `/admin/deliveries?${qs}` : "/admin/deliveries",
  );
  return res.data;
}

export async function fetchDeliveryDetail(id: string): Promise<DeliveryDetail> {
  const res = await apiFetch<{ data: DeliveryDetail }>(
    `/admin/deliveries/${id}`,
  );
  return res.data;
}

export async function reindexDelivery(
  id: string,
  reason: string,
): Promise<void> {
  await apiFetch<{ data: { reindex_queued: boolean } }>(
    `/admin/deliveries/${id}/reindex`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function unhideDelivery(
  id: string,
  reason: string,
): Promise<void> {
  await apiFetch<{ data: { unhidden: boolean } }>(
    `/admin/deliveries/${id}/unhide`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function unpinDelivery(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { unpinned: boolean } }>(
    `/admin/deliveries/${id}/unpin`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}
